import { Box, useTheme } from "@mui/material";
import { DropdownMenu } from "components";
import { memo, useCallback } from "react";

interface MemberTagProps {
  level: "ticket" | "thread";
  className?: string;
  /** When provided, the tag becomes a dropdown to switch levels */
  onLevelChange?: (level: "ticket" | "thread") => void;
}

const DropdownArrow = () => (
  <svg width="4" height="8" viewBox="0 0 4 8" fill="none" aria-hidden="true">
    <title>Expand</title>
    <path d="M0 0L4 4L0 8V0Z" fill="currentColor" />
  </svg>
);

const LEVEL_OPTIONS = [
  { name: "Ticket level", value: "ticket" },
  { name: "Thread level", value: "thread" },
];

/**
 * MemberTag - Displays membership level indicator
 * - Ticket Level: Blue background
 * - Thread Level: Yellow background
 * - When onLevelChange is provided, uses DropdownMenu to switch levels
 */
export const MemberTag = memo(
  ({ level, className, onLevelChange }: MemberTagProps) => {
    const theme = useTheme();

    const isTicketLevel = level === "ticket";
    const isInteractive = Boolean(onLevelChange);

    const handleOptionClick = useCallback(
      (option: { value?: string }) => {
        const newLevel = option.value as "ticket" | "thread";
        if (newLevel && newLevel !== level) {
          onLevelChange?.(newLevel);
        }
      },
      [level, onLevelChange],
    );

    const tagContent = (
      <Box
        className={className}
        sx={{
          height: "24px",
          padding: "4px 8px",
          borderRadius: "4px",
          display: "flex",
          alignItems: "center",
          gap: "4px",
          backgroundColor: isTicketLevel
            ? theme.palette.primitive.system.blue[200]
            : theme.palette.primitive.system.yellow[50],
          color: theme.palette.lightBg.high,
          cursor: isInteractive ? "pointer" : "default",
          ...theme.typography["caption-xs"],
        }}
      >
        {isTicketLevel ? "Ticket level" : "Thread level"}
        {isInteractive && <DropdownArrow />}
      </Box>
    );

    if (!isInteractive) return tagContent;

    return (
      <DropdownMenu
        options={LEVEL_OPTIONS}
        selectedValue={level}
        onOptionClick={handleOptionClick}
        selectedCheckIcon
        paperSx={{ minWidth: "140px" }}
      >
        {tagContent}
      </DropdownMenu>
    );
  },
);

MemberTag.displayName = "MemberTag";
